<template>
  <div v-click-outside="onCloseChat" class="fill-height">
    <NewChatDialog
      v-if="newGroupType"
      :selection-array="chatGroupArray"
      :creating-d-m="creatingDMGroup"
      @create-group="createGroup"
      @close-dialog="closeCreateChatDialog"
    />

    <ChatWindowPanel
      v-if="selectedChannelId"
      :title="chatTitle"
      :channel-id="selectedChannelId"
      :key="selectedChannelId"
      @close-chat="onCloseChat"
    />

    <template v-else>
      <div id="channelsList-container">
        <ChannelsList :channels="store.filteredChannels" @channel-clicked="channelClicked" />
      </div>
      <v-app-bar dark dense>
        <v-text-field
          class="ml-0 pt-0"
          dark
          color="amber"
          prepend-icon="search"
          clearable
          single-line
          hide-details
          label="Search"
          v-model="store.searchText"
        />
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn icon v-on="on" @click="createDM">
              <v-icon>add</v-icon>
            </v-btn>
          </template>
          <span>New chat group</span>
        </v-tooltip>
      </v-app-bar>
    </template>
  </div>
</template>

<script setup lang="ts">
import ChannelsList from './ChannelsList.vue'
import NewChatDialog from './NewChatDialog.vue'
import ChatWindowPanel from '../common/chat/ChatWindowPanel.vue'
import { Timestamp } from 'firebase/firestore'
import { getHostName, parseGroupId } from '@/utils/chat'
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useGetters, useActions } from 'vuex-composition-helpers'
import { useChatStore } from '../common/chat/useChatData'
import { Channel } from '../common/chat/models'
import { useCurrentUser } from '@/common/useCurrentUser'

type GroupType = 'DM' | 'Unit'

const store = useChatStore()
const user = useCurrentUser()

const { getVisibleUsers: usersArray } = useGetters('users', ['getVisibleUsers'])

const { fetchAllUsers } = useActions('users', ['fetchAllUsers'])
const { fetchUnitNumbers } = useActions('unitNumbers', ['fetchUnitNumbers'])

const selectedChannelId = ref<string>()
const chatTitle = ref<string>()
const newGroupType = ref<GroupType>()

const chatGroupArray = computed(() => {
  if (newGroupType.value === 'DM') {
    return usersArray.value
  }

  return []
})

const creatingDMGroup = computed(() => {
  return newGroupType.value === 'DM'
})

const channelClicked = ({ documentId, group, unsubscribed }) => {
  if (unsubscribed) {
    createGroupForUnsubscribedChannel(parseGroupId(documentId).id)
  }
  chatTitle.value = group
  selectedChannelId.value = documentId
}

const onCloseChat = () => {
  selectedChannelId.value = undefined
  chatTitle.value = ''
}

const constructChatInfo = () => {
  const userSid = user.value.userSid

  return {
    participants: [userSid],
    recent: null,
    public: false,
    version: 1,
    subdomain: getHostName(),
    read: {
      [userSid]: Timestamp.now(),
    },
  } as Channel
}

const createGroupForUnsubscribedChannel = (unitId) => {
  const group = constructChatInfo()
  group.public = true

  store.createNewChatGroup(group, unitId)
}

const createGroup = ({ selected }) => {
  const group = constructChatInfo()

  if (newGroupType.value === 'DM') {
    group.participants = [...group.participants, ...selected.map((s) => s.id)]

    return store.createNewChatGroup(group)
  } else {
    return false
  }
}

const createDM = () => {
  newGroupType.value = 'DM'
}

const closeCreateChatDialog = () => {
  newGroupType.value = undefined
}

onMounted(() => {
  fetchAllUsers()
  fetchUnitNumbers()
})
</script>

<style>
#channelsList-container {
  height: 450px;
}
</style>
